import { useState, useCallback, useRef, useEffect } from "react"
import { useDispatch } from "react-redux"
import { normalizeMessage, removeFirstBr } from "../utils/parsers/messages"
import { extractMainMessage } from "../../utils/serializers/chat"
import { READ_MESSAGE_STATUS, UNREAD_MESSAGE_STATUS } from "../../utils/variables/chat"
import { bumpChat, adjustUnreadCount } from "../../../app/reducers/book/notifies"

export const formatTime = () => {
  const d = new Date()
  const pad = (n) => String(n).padStart(2, "0")
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} | ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`
}

const useChatMessages = ({
  departments,
  getChatDetail,
  createNotify,
  locale,
  currentCompanyId,
  currentUserId,
  sortByLastMessage,
}) => {
  const dispatch = useDispatch()

  const getChatDetailRef = useRef(getChatDetail)
  useEffect(() => {
    getChatDetailRef.current = getChatDetail
  }, [getChatDetail])

  const createNotifyRef = useRef(createNotify)
  useEffect(() => {
    createNotifyRef.current = createNotify
  }, [createNotify])

  const departmentsRef = useRef(departments)
  useEffect(() => {
    departmentsRef.current = departments
  }, [departments])

  const localeRef = useRef(locale)
  useEffect(() => {
    localeRef.current = locale
  }, [locale])

  const currentCompanyIdRef = useRef(currentCompanyId)
  useEffect(() => {
    currentCompanyIdRef.current = currentCompanyId
  }, [currentCompanyId])

  const [chatMessages, setChatMessages] = useState({})
  const [loadingChats, setLoadingChats] = useState({})
  const [errorChats, setErrorChats] = useState({})

  const buildDepartmentMap = () => {
    const map = new Map()
    departmentsRef.current?.forEach((d) => {
      if (d?.id != null) map.set(d.id, d.name ?? "")
    })
    return map
  }

  // вынесенный маппер — переиспользуется и при первичной загрузке (fetchMessages),
  // и при live-сообщении (receiveMessage). Поддерживает и формат бандл-чатов,
  // и формат лид-чатов/AI-бота (определяется по наличию lead_id / role).
  const mapNotify = useCallback((msg, { departmentMap, curCompanyId, bundleId }) => {
    const isAiBotMsg = msg.role != null

    return {
      id: msg.id,
      // у лидов — user_name, у бандлов — name
      sender: isAiBotMsg ? (msg.role === "bot" ? "AI Bot" : "") : (msg.user_name ?? msg.name ?? ""),
      company: msg.company_name ?? "",
      text: extractMainMessage(isAiBotMsg ? msg.content : msg.message),
      time: (() => {
        if (!msg.created_at) return ""
        const d = new Date(msg.created_at)
        if (isNaN(d.getTime())) return ""
        const pad = (n) => String(n).padStart(2, "0")
        return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} | ${pad(d.getHours())}:${pad(d.getMinutes())}`
      })(),
      status: isAiBotMsg
        ? READ_MESSAGE_STATUS
        : msg.is_read
          ? READ_MESSAGE_STATUS
          : UNREAD_MESSAGE_STATUS,
      isOwn: isAiBotMsg ? msg.role === "bot" : msg.user_company_id === curCompanyId,
      isEditing: false,
      isHidden: msg.is_hidden ?? false,
      files:
        Array.isArray(msg.files) && msg.files.length > 0
          ? msg.files.map((f) => ({
              url: f.url ?? "",
              file_name: f.file_name ?? "",
            }))
          : [],
      // у лидов — lead_id вместо bundle_id
      bundleId: msg.bundle_id ?? msg.lead_id ?? (isAiBotMsg ? bundleId : null),
      department: departmentMap.get(msg.departament_id) ?? "",
      department_id: msg.departament_id ?? null,
      company_id: msg.user_company_id ?? null,
      avatar: msg.avatar ?? null,
      for_admin: msg.for_admin ?? false,
      read_by: msg?.read_log ?? [],
      reply_to_id: msg.reply_to_id ?? null,
      from_partner: msg?.provider_id > 0 || msg?.is_provider_notify === true,
      is_system:
        !isAiBotMsg &&
        !msg?.user_id &&
        !msg?.manager_id &&
        !(msg?.provider_id > 0) &&
        !msg?.is_provider_notify,
      // дополнительные поля лида
      lead_id: msg.lead_id ?? null,
      lead_name: msg.lead_name ?? null,
      lead_status_name: msg.lead_status_name ?? null,
      lead_status_color: msg.lead_status_color ?? null,
      lead_status_bg: msg.lead_status_bg ?? null,
      is_hidden: msg?.is_hidden ?? false,
      message_theme: msg?.message_theme ?? null,
    }
  }, [])

  // fetcherOverride позволяет явно передать нужный fetcher (например getLeadChatDetail)
  // минуя ref, который может быть ещё не обновлён после смены activeTab
  const fetchMessages = useCallback(
    async (bundleId, isSendMsg = false, fetcherOverride = null) => {
      if (!bundleId) return

      if (!isSendMsg) {
        setLoadingChats((prev) => ({ ...prev, [bundleId]: true }))
        setChatMessages((prev) => ({ ...prev, [bundleId]: [] }))
      }
      setErrorChats((prev) => ({ ...prev, [bundleId]: null }))

      try {
        const fetcher = fetcherOverride ?? getChatDetailRef.current
        const response = await fetcher({ bundle_id: bundleId }).unwrap()

        // Лид-чаты: { data: [...], meta: {...} }
        // Бандл-чаты: { notifies: [...] }
        const rawList = response.data ?? response.notifies ?? response.messages ?? []

        const departmentMap = buildDepartmentMap()
        const curCompanyId = currentCompanyIdRef.current

        const mapped = (Array.isArray(rawList) ? rawList : []).map((msg) =>
          mapNotify(msg, { departmentMap, curCompanyId, bundleId })
        )

        if (isSendMsg) {
          setChatMessages((prev) => {
            const existing = prev[bundleId] ?? []
            const existingIds = new Set(existing.map((m) => m.id))
            const newMessages = mapped.filter((m) => !existingIds.has(m.id))
            if (newMessages.length === 0) return prev
            return { ...prev, [bundleId]: [...existing, ...newMessages] }
          })
        } else {
          setChatMessages((prev) => ({ ...prev, [bundleId]: mapped }))
        }
      } catch (err) {
        console.error("fetchMessages error", err)
        setErrorChats((prev) => ({ ...prev, [bundleId]: err }))
      } finally {
        if (!isSendMsg) {
          setLoadingChats((prev) => ({ ...prev, [bundleId]: false }))
        }
      }
    },
    [mapNotify]
  )

  // приход сообщения через ActionCable для открытого сейчас чата
  const receiveMessage = useCallback(
    (bundleId, rawMsg) => {
      if (!bundleId || !rawMsg) return

      const departmentMap = buildDepartmentMap()
      const curCompanyId = currentCompanyIdRef.current
      const mapped = mapNotify(rawMsg, { departmentMap, curCompanyId, bundleId })

      setChatMessages((prev) => {
        const existing = prev[bundleId] ?? []
        if (existing.some((m) => m.id === mapped.id)) return prev
        return { ...prev, [bundleId]: [...existing, mapped] }
      })
    },
    [mapNotify]
  )

  // приход read-статуса через ActionCable
  const applyReadStatus = useCallback((bundleId, notifyId, isRead, readLogHistory) => {
    setChatMessages((prev) => ({
      ...prev,
      [bundleId]: (prev[bundleId] ?? []).map((m) =>
        m.id === notifyId
          ? {
              ...m,
              status: isRead ? READ_MESSAGE_STATUS : UNREAD_MESSAGE_STATUS,
              read_by: readLogHistory ?? m.read_by,
            }
          : m
      ),
    }))
  }, [])

  const getMessages = useCallback((bundleId) => chatMessages[bundleId] ?? [], [chatMessages])
  const isLoading = useCallback((bundleId) => loadingChats[bundleId] ?? false, [loadingChats])
  const getError = useCallback((bundleId) => errorChats[bundleId] ?? null, [errorChats])

  // Без оптимистичного пуша: сообщение добавляется в стейт только когда придёт
  // через ActionCable (receiveMessage, дедуп по id) либо через fetchMessages(..., true)
  // ниже (тоже дедуп по id). Это убирает дубль у отправителя.
  const sendMessage = useCallback(
    async (
      bundleId,
      {
        text,
        department,
        attachedFiles,
        theme,
        for_admin = false,
        reply_to_id = null,
        provider_id,
        remote_order,
      }
    ) => {
      if (!text.trim() && !attachedFiles?.length) return

      const payload = {
        bundle_id: bundleId,
        message: text.trim(),
        departament_id: department,
        provider_id: provider_id,
        for_admin: for_admin,
      }

      if (remote_order) payload.remote_order = remote_order
      if (reply_to_id) payload.reply_to_id = reply_to_id
      if (attachedFiles?.length) payload.files = attachedFiles
      if (theme) payload.type = theme

      try {
        await createNotifyRef.current(payload).unwrap()

        if (sortByLastMessage) {
          dispatch(
            bumpChat({
              bundle_id: bundleId,
              message: text.trim(),
              created_at: new Date().toISOString(),
            })
          )
        }

        // Подстраховка на случай, если сокет-событие не долетело/опоздало —
        // дедуп по id внутри fetchMessages(bundleId, true) не даст дубля.
        await fetchMessages(bundleId, true)
      } catch (err) {
        console.error("sendMessage error", err)
      }
    },
    [fetchMessages, sortByLastMessage, dispatch]
  )

  const toggleEdit = useCallback((bundleId, msgId) => {
    setChatMessages((prev) => ({
      ...prev,
      [bundleId]: (prev[bundleId] ?? []).map((m) =>
        m.id === msgId ? { ...m, isEditing: true, editDraft: m.text } : m
      ),
    }))
  }, [])

  const saveEdit = useCallback(async (bundleId, msgId, newText, updateNotifyMessage) => {
    if (!newText.trim()) return

    try {
      await updateNotifyMessage({ id: msgId, message: newText.trim() }).unwrap()

      setChatMessages((prev) => ({
        ...prev,
        [bundleId]: (prev[bundleId] ?? []).map((m) =>
          m.id === msgId
            ? { ...m, text: newText.trim(), isEditing: false, edited: true, editDraft: undefined }
            : m
        ),
      }))
    } catch (err) {
      console.error("saveEdit error", err)
    }
  }, [])

  const cancelEdit = useCallback((bundleId, msgId) => {
    setChatMessages((prev) => ({
      ...prev,
      [bundleId]: (prev[bundleId] ?? []).map((m) =>
        m.id === msgId ? { ...m, isEditing: false, editDraft: undefined } : m
      ),
    }))
  }, [])

  const changeDept = useCallback((bundleId, msgId, newDeptId, newDeptName) => {
    setChatMessages((prev) => ({
      ...prev,
      [bundleId]: (prev[bundleId] ?? []).map((m) =>
        m.id === msgId ? { ...m, department: newDeptName, department_id: newDeptId } : m
      ),
    }))
  }, [])

  const toggleReadStatus = useCallback(
    async (bundleId, msgId, { toggleReadMsg, currentUserId }) => {
      const messages = chatMessages[bundleId] ?? []
      const targetMsg = messages.find((m) => m.id === msgId)
      if (!targetMsg) return

      const isCurrentlyRead = targetMsg.status === READ_MESSAGE_STATUS
      const newStatus = isCurrentlyRead ? UNREAD_MESSAGE_STATUS : READ_MESSAGE_STATUS
      const delta = isCurrentlyRead ? 1 : -1

      // Оптимистично обновляем статус сообщения
      setChatMessages((prev) => ({
        ...prev,
        [bundleId]: (prev[bundleId] ?? []).map((m) =>
          m.id === msgId ? { ...m, status: newStatus } : m
        ),
      }))

      // Оптимистично обновляем счётчик в сайдбаре
      dispatch(
        adjustUnreadCount({
          bundle_id: targetMsg.lead_id ? undefined : bundleId,
          lead_id: targetMsg.lead_id ?? undefined,
          delta,
        })
      )

      try {
        const response = await toggleReadMsg(msgId).unwrap()

        const readLog = response.read_log
        if (readLog) {
          setChatMessages((prev) => ({
            ...prev,
            [bundleId]: (prev[bundleId] ?? []).map((m) => {
              if (m.id !== msgId) return m
              return { ...m, read_by: readLog.history ?? [] }
            }),
          }))
        }

        const actualStatus = response.is_read ? READ_MESSAGE_STATUS : UNREAD_MESSAGE_STATUS
        if (actualStatus !== newStatus) {
          // Сервер вернул другой статус — корректируем обратно
          dispatch(
            adjustUnreadCount({
              bundle_id: targetMsg.lead_id ? undefined : bundleId,
              lead_id: targetMsg.lead_id ?? undefined,
              delta: -delta,
            })
          )
          setChatMessages((prev) => ({
            ...prev,
            [bundleId]: (prev[bundleId] ?? []).map((m) =>
              m.id === msgId ? { ...m, status: actualStatus } : m
            ),
          }))
        }
      } catch (err) {
        console.error("toggleReadStatus error", err)

        // Откатываем оптимистичные изменения
        dispatch(
          adjustUnreadCount({
            bundle_id: targetMsg.lead_id ? undefined : bundleId,
            lead_id: targetMsg.lead_id ?? undefined,
            delta: -delta,
          })
        )
        setChatMessages((prev) => ({
          ...prev,
          [bundleId]: (prev[bundleId] ?? []).map((m) =>
            m.id === msgId ? { ...m, status: targetMsg.status } : m
          ),
        }))
      }
    },
    [chatMessages, dispatch]
  )

  const markAsRead = useCallback(
    async (bundleId, { toggleReadMsg }) => {
      let unread = []

      setChatMessages((prev) => {
        const messages = prev[bundleId] ?? []
        unread = messages.filter((m) => m.status !== READ_MESSAGE_STATUS && !m.isOwn)
        if (unread.length === 0) return prev

        return {
          ...prev,
          [bundleId]: messages.map((m) =>
            unread.some((u) => u.id === m.id) ? { ...m, status: READ_MESSAGE_STATUS } : m
          ),
        }
      })

      await Promise.resolve()
      if (unread.length === 0) return

      dispatch(
        adjustUnreadCount({
          bundle_id: unread[0]?.lead_id ? undefined : bundleId,
          lead_id: unread[0]?.lead_id ?? undefined,
          delta: -unread.length,
        })
      )

      const results = await Promise.allSettled(unread.map((msg) => toggleReadMsg(msg.id).unwrap()))

      results.forEach((result, i) => {
        if (result.status !== "fulfilled" || !result.value) return
        const response = result.value
        const readLog = response.read_log
        if (!readLog) return

        const msgId = unread[i].id
        setChatMessages((prev) => ({
          ...prev,
          [bundleId]: (prev[bundleId] ?? []).map((m) =>
            m.id === msgId ? { ...m, read_by: readLog.history ?? [] } : m
          ),
        }))
      })
    },
    [dispatch]
  )

  return {
    getMessages,
    fetchMessages,
    isLoading,
    getError,
    sendMessage,
    toggleEdit,
    saveEdit,
    cancelEdit,
    changeDept,
    toggleReadStatus,
    markAsRead,
    receiveMessage,
    applyReadStatus,
  }
}

export default useChatMessages
